import {
  Alert,
  Button,
  Collapse,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tooltip,
  message,
} from "antd";
import cx from "classnames";
import PropTypes from "prop-types";
// eslint-disable-next-line no-unused-vars
import React, { useCallback, useState } from "react";
import { t } from "ttag";

import CodeDiff from "metabase/jerry/CodeDiff";
import { Icon } from "metabase/ui";

const { Option } = Select;
const { Panel } = Collapse;

const sqlViewerStyle = {
  backgroundColor: "#f7f7f9", // A light, neutral background
  border: "1px solid #d9d9d9",
  borderRadius: "4px",
  padding: "12px",
  maxHeight: "200px",
  overflowY: "auto",
};

const preTagStyle = {
  margin: 0, // Reset default margins
  whiteSpace: "pre-wrap", // Wrap long lines of code
  wordBreak: "break-all", // Break long words or identifiers
  fontFamily: "Menlo, Monaco, 'Courier New', monospace",
  fontSize: "13px",
  color: "#333", // Dark grey for text
};

const AISQL = ({ editor, className, size, user, sqlError, onSQLChange }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [generatedSql, setGeneratedSql] = useState(null);
  const [originalSql, setOriginalSql] = useState(null);
  const [isOptimizationMode, setIsOptimizationMode] = useState(false);
  const [currentError, setCurrentError] = useState(null);

  // --- NEW STATES ---
  const [aiModel, setAiModel] = useState(["o3-mini"]); // State for AI model selection
  // States for the History feature
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);

  // States for the SQL Diff modal
  const [isDiffModalVisible, setIsDiffModalVisible] = useState(false);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);

  const showModal = useCallback(() => {
    const currentEditorSql = editor?.getValue() ?? "";
    const isOptimizing = !!currentEditorSql.trim();
    setIsOptimizationMode(isOptimizing);
    setOriginalSql(currentEditorSql);
    if (isOptimizing) {
      setCurrentError(sqlError);
    } else {
      setCurrentError(null);
    }

    setInputValue("");
    setGeneratedSql(null);
    setIsLoading(false); // Ensure loading is reset
    setIsHistoryVisible(false);
    setIsModalVisible(true);
  }, [editor, sqlError]);

  const handleCancel = useCallback(() => {
    setIsModalVisible(false);
    // Optionally reset input value on cancel if desired
    // setInputValue("");
  }, []);

  const handleInputChange = useCallback((e) => {
    setInputValue(e.target.value || "");
  }, []);

  const handleSend = useCallback(async () => {
    // Trim input to check if it's just whitespace
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) {
      message.warning(t`Please enter a description first.`);
      return; // Don't send if input is empty or only whitespace
    }

    setIsHistoryVisible(false);
    setIsLoading(true);
    // setGeneratedSql(null); // Reset previous results if needed

    try {
      const currentEditorSql = editor?.getValue() ?? "";
      const requestBody = {
        user: user,
        model: aiModel && aiModel.length > 0 ? aiModel[0] : "o3-mini",
        message: [
          {
            role: "user",
            content: trimmedInput,
          },
        ],
        ...(currentEditorSql.trim() && { query: currentEditorSql }),
        ...(currentError && { error: currentError }),
      };
      const response = await fetch(`/api/jerry/extend`, {
        // Replace with your actual API endpoint
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Add any other necessary headers like Authorization if needed
        },
        body: JSON.stringify({
          call: "post",
          service_name: "ai_sql",
          body: requestBody,
          timeout: 120000,
        }),
      });

      if (!response.ok) {
        // Try to get error message from backend response body
        let errorDetail = `HTTP error ${response.status}`;
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorData.message || errorDetail;
        } catch (e) {
          // Ignore if response body is not JSON or empty
        }
        throw new Error(errorDetail);
      }

      const result = await response.json();

      if (result?.error) {
        console.error("AI returned an error:", result.error);
        throw new Error(t`AI response error: ${result.error}`);
      }

      const newSql =
        result.new_sql || (typeof result === "string" ? result : ""); // Extract SQL safely
      if (typeof newSql !== "string") {
        console.error(
          "API response did not contain a valid SQL string:",
          result,
        );
        throw new Error(t`Received invalid format from AI.`);
      }
      setGeneratedSql(newSql);

      message.success(t`SQL generated successfully!`);
      // setIsModalVisible(false); // Close modal on success
      // setInputValue(""); // Clear input field on success
      setIsHistoryVisible(false); // Hide history after successful generation
    } catch (error) {
      console.error("Error fetching generated SQL:", error);
      message.error(t`Failed to generate SQL, please try again`);
      setGeneratedSql(null);
      setOriginalSql(null);
    } finally {
      setIsLoading(false); // Ensure loading state is turned off
    }
  }, [inputValue, editor, user, aiModel, currentError]);

  // const handleKeyPress = useCallback((e) => {
  //   // Check if Enter is pressed (without Shift for multiline input)
  //   // and if the input is not empty and not currently loading
  //   if (e.key === 'Enter' && !e.shiftKey && inputValue.trim() && !isLoading) {
  //     e.preventDefault(); // Prevent default Enter behavior (like adding a newline)
  //     handleSend(); // Trigger the send action
  //   }
  // }, [handleSend, inputValue, isLoading]);

  const isSendDisabled = !inputValue.trim() || isLoading;
  const showDiffView = generatedSql !== null && !isLoading;

  const handleDone = useCallback(() => {
    if (generatedSql !== null && onSQLChange) {
      try {
        onSQLChange(generatedSql);
        message.success(t`SQL updated successfully!`);
        setIsModalVisible(false); // Close modal after applying
      } catch (error) {
        console.error("Error setting editor value:", error);
        message.error(t`Failed to update editor content.`);
      }
    } else {
      message.error(
        t`Cannot apply SQL. No generated SQL or editor unavailable.`,
      );
      console.error("handleDone called incorrectly:", {
        generatedSql,
        onSQLChange,
      });
    }
  }, [generatedSql, onSQLChange]);

  const handleBackToEdit = useCallback(() => {
    setGeneratedSql(null);
  }, []);

  const handleHistoryIconClick = async () => {
    const willBeVisible = !isHistoryVisible;
    setIsHistoryVisible(willBeVisible);

    if (willBeVisible) {
      setIsHistoryLoading(true);
      try {
        // NOTE: Replace with your actual API endpoint
        const response = await fetch(`/api/jerry/extend`, {
          // Replace with your actual API endpoint
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Add any other necessary headers like Authorization if needed
          },
          body: JSON.stringify({
            call: "get",
            service_name: "ai_sql_history",
            params: {
              user: user?.email || "",
            },
            timeout: 60000,
          }),
        });
        if (!response.ok) {
          throw new Error("Failed to fetch history");
        }
        const responseData = await response.json();
        const formattedData = responseData.map((item) => ({
          id: item.id,
          prompt: item.request_body?.message?.[0]?.content ?? "", // Safely access nested prompt
          timestamp: item.created_at,
          model: item.model,
          base_sql: item.request_body?.query ?? "", // Safely access base_sql or default to empty
          // Safely access the nested new_sql, handling if 'ret' is a string or object
          output_sql:
            item.answer?.ret?.new_sql ??
            (typeof item.answer?.ret === "string" ? item.answer.ret : ""),
        }));
        setHistoryData(formattedData);
      } catch (error) {
        console.error("Error fetching history:", error);
        message.error(t`Could not load history: ${error.message}`);
        setIsHistoryVisible(false); // Close on error
      } finally {
        setIsHistoryLoading(false);
      }
    }
  };

  const handleShowSqlClick = (record) => {
    setSelectedHistoryItem(record);
    setIsDiffModalVisible(true);
  };

  const handleDiffModalCancel = () => {
    setIsDiffModalVisible(false);
    setSelectedHistoryItem(null); // Clear selected item on close
  };

  const handleHistoryRowClick = (record) => {
    setInputValue(record?.prompt || "");
    if (record?.output_sql && onSQLChange) {
      try {
        onSQLChange(record.output_sql);
      } catch (error) {
        console.error("Error set history output SQL value:", error);
        message.error(t`Failed to update editor content.`);
      }
    }
    setIsHistoryVisible(false); // Hide history after selecting
    message.info(t`Loaded prompt from history.`);
  };

  const historyColumns = [
    { title: t`Prompt`, dataIndex: "prompt", key: "prompt", ellipsis: true },
    // { title: t`Model`, dataIndex: "model", key: "model", width: 120 },
    {
      title: t`Timestamp`,
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
      render: (isoString) => {
        if (!isoString) {
          return "N/A"; // Handle cases where timestamp might be null
        }
        const date = new Date(isoString);

        const year = date.getFullYear();
        // getMonth() is 0-indexed, so we add 1. Pad with '0' if it's a single digit.
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const seconds = String(date.getSeconds()).padStart(2, "0");

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      },
    },
    {
      title: t`Actions`,
      key: "actions",
      width: 120,
      // eslint-disable-next-line react/display-name
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={(e) => {
            e.stopPropagation(); // This line prevents the row's onClick from firing
            handleShowSqlClick(record);
          }}
          disabled={!record.base_sql && !record.output_sql}
        >
          {t`Show SQL`}
        </Button>
      ),
    },
  ];

  const getModalFooter = () => {
    const leftSide = (
      <div
        key="left-side"
        style={{ display: "flex", alignItems: "center", gap: "8px" }}
      >
        <Select
          showSearch
          mode="tags"
          maxCount={1}
          value={aiModel}
          onChange={setAiModel}
          disabled={isLoading}
          style={{ width: "140px" }}
        >
          <Option value="o1-mini">o1-mini</Option>
          <Option value="o3-mini">o3-mini</Option>
          <Option value="o4-mini">o4-mini</Option>
          <Option value="o3">o3</Option>
          <Option value="o1">o1</Option>
          <Option value="gemini-2.5-pro">gemini-2.5-pro</Option>
          <Option value="gemini-2.5-flash">gemini-2.5-flash</Option>
        </Select>
        {!showDiffView && (
          <Tooltip title={t`History`}>
            <a
              className="text-brand-hover"
              style={{
                display: "flex",
                // Set a clearer default color, which turns to brand blue when active
                color: isHistoryVisible ? "#509ee3" : "#848E9A",
                marginLeft: "5px",
              }}
            >
              <Icon
                name="history"
                size={20}
                onClick={handleHistoryIconClick}
                disabled={isLoading}
              />
            </a>
          </Tooltip>
        )}
      </div>
    );

    const rightSide = showDiffView
      ? [
          <Button
            key="back"
            onClick={handleCancel}
            style={{ marginLeft: "8px" }}
          >
            {t`Cancel`}
          </Button>,
          <Button
            key="edit"
            onClick={handleBackToEdit}
            style={{ marginLeft: "8px" }}
          >
            {t`Back to Edit`}
          </Button>,
          <Button
            key="done"
            type="primary"
            onClick={handleDone}
            disabled={!onSQLChange}
            style={{ marginLeft: "8px" }}
          >
            {t`Apply`}
          </Button>,
        ]
      : [
          <Button key="back" onClick={handleCancel} disabled={isLoading}>
            {t`Cancel`}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isLoading}
            onClick={handleSend}
            disabled={isSendDisabled}
            style={{ marginLeft: "8px" }}
          >
            {isLoading ? t`Generating...` : t`Submit`}
          </Button>,
        ];

    return (
      // Use a flex container to push left and right sides apart
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {leftSide}
        <div>{rightSide}</div>
      </div>
    );
  };

  const modalTitle = isOptimizationMode
    ? t`Optimize SQL with AI`
    : t`Generate SQL with AI`;
  const placeholderText = isOptimizationMode
    ? t`Describe how to optimize or fix the SQL... (e.g., 'Fix the syntax error' or 'Help me optimize this SQL')`
    : t`Describe what data you want to query... (e.g., 'Show monthly policy sales for the past 6 months.')`;
  const panelHeaderText = currentError
    ? t`Show Current SQL & Error`
    : t`Show Current SQL`;

  return (
    <>
      <Tooltip title={t`Open Metabase SQL AI`}>
        <a
          className={cx(className, "transition-color text-brand-hover")}
          style={{ marginBottom: "-6px" }}
        >
          <Icon name="metabot" size={size} onClick={showModal} />
        </a>
      </Tooltip>

      <Modal
        title={modalTitle}
        width={showDiffView ? "90%" : "60%"}
        open={isModalVisible}
        onCancel={handleCancel} // Handle closing the modal
        // We use a custom footer for more control over the Send button
        footer={getModalFooter()}
        styles={{
          body: {
            maxHeight: "65vh",
            overflowY: "auto",
          },
        }}
      >
        {isLoading && (
          // Loading State
          <div style={{ textAlign: "center", padding: "30px 0" }}>
            <Spin size="large" />
            <p
              style={{ marginTop: "10px" }}
            >{t`Generating SQL, please wait...`}</p>
          </div>
        )}

        {!isLoading && !showDiffView && (
          <>
            {isOptimizationMode && (
              <Collapse ghost style={{ marginBottom: 16 }}>
                <Panel header={panelHeaderText} key="1">
                  <div style={sqlViewerStyle}>
                    <pre style={preTagStyle}>{originalSql}</pre>
                  </div>
                  {currentError && (
                    <Alert
                      message={t`Last Known Error`}
                      description={
                        <pre style={preTagStyle}>{currentError}</pre>
                      }
                      type="error"
                      style={{ marginTop: 12 }}
                      showIcon
                    />
                  )}
                </Panel>
              </Collapse>
            )}
            <Input.TextArea
              rows={6}
              placeholder={placeholderText}
              value={inputValue}
              onChange={handleInputChange}
              disabled={isLoading}
            />
          </>
        )}

        {showDiffView && (
          // Diff View State
          <>
            <Alert
              message={t`Generated SQL based on your request.`}
              description={t`Review the differences below. Click 'Apply' to replace the editor content or 'Cancel' to discard.`}
              type="info"
              showIcon
              style={{ marginBottom: "10px" }}
            />
            <CodeDiff
              oldValue={originalSql ?? ""}
              newValue={generatedSql ?? ""}
            />
          </>
        )}

        {isHistoryVisible && !showDiffView && (
          <div style={{ marginTop: "20px" }}>
            <Table
              size="small"
              loading={isHistoryLoading} // Show loading spinner on the table
              columns={historyColumns}
              dataSource={historyData}
              rowKey="id" // Assuming timestamp is unique, or use another unique ID
              scroll={{ y: 200 }}
              pagination={{ pageSize: 10 }}
              onRow={(record) => ({
                onClick: () => handleHistoryRowClick(record),
              })}
              rowClassName={() => "cursor-pointer"}
            />
          </div>
        )}
      </Modal>

      <Modal
        title={t`SQL Difference`}
        open={isDiffModalVisible}
        onCancel={handleDiffModalCancel}
        width={"90%"}
        footer={[
          <Button key="close" onClick={handleDiffModalCancel}>
            {t`Close`}
          </Button>,
        ]}
        styles={{
          body: {
            maxHeight: "70vh",
            overflowY: "auto",
          },
        }}
      >
        {selectedHistoryItem && (
          <CodeDiff
            oldValue={selectedHistoryItem.base_sql || ""}
            newValue={selectedHistoryItem.output_sql || ""}
          />
        )}
      </Modal>
    </>
  );
};

AISQL.propTypes = {
  editor: PropTypes.object,
  className: PropTypes.string,
  size: PropTypes.number,
  user: PropTypes.object,
  sqlError: PropTypes.string,
  onSQLChange: PropTypes.func,
};

export default AISQL;
